import { Link, useRouteContext } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
    Bell, ChartColumnBig, CircleUserRound, HandHeart, House, LifeBuoy, MessageCircle, Settings, Users, type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { noticesQuery, threadsQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

/* Spec PWA-001 — navegacao em telas largas (>= lg): menu lateral fixo no lugar da barra inferior.
   Mesmos destinos da barra do celular (Inicio, Mensagens, Doar, Impacto, Conta) + Criancas, que no
   celular fica no Inicio/Conta, e os atalhos de Comunicados, Configuracoes e Ajuda. */

type Item = { to: '/inicio' | '/mensagens' | '/criancas' | '/impacto' | '/conta' | '/comunicados' | '/configuracoes' | '/ajuda'; labelKey: string; Icon: LucideIcon; exact: boolean }

const PRIMARY: Item[] = [
    { to: '/inicio', labelKey: 'sponsorShell.nav.home', Icon: House, exact: true },
    { to: '/mensagens', labelKey: 'sponsorShell.nav.messages', Icon: MessageCircle, exact: false },
    { to: '/criancas', labelKey: 'sponsorShell.nav.children', Icon: Users, exact: false },
    { to: '/impacto', labelKey: 'sponsorShell.nav.impact', Icon: ChartColumnBig, exact: true },
    { to: '/conta', labelKey: 'sponsorShell.nav.account', Icon: CircleUserRound, exact: true },
]

const SECONDARY: Item[] = [
    { to: '/comunicados', labelKey: 'sponsorHome.notices', Icon: Bell, exact: true },
    { to: '/configuracoes', labelKey: 'sponsorHome.settings', Icon: Settings, exact: true },
    { to: '/ajuda', labelKey: 'sponsorShell.nav.help', Icon: LifeBuoy, exact: true },
]

function NavItem({ item, badge, badgeLabel }: { item: Item; badge?: number; badgeLabel?: string }) {
    const { t } = useTranslation()
    const { to, labelKey, Icon, exact } = item
    return (
        <Link
            to={to}
            activeOptions={{ exact }}
            className="group flex min-h-11 items-center gap-3 rounded-xl px-3 text-[0.92rem] font-semibold text-ink-muted no-underline hover:bg-ink/5 hover:text-ink data-[status=active]:bg-brand-soft data-[status=active]:font-bold data-[status=active]:text-brand-strong"
        >
            <Icon className="size-5 shrink-0" aria-hidden="true" />
            <span className="grow">{t(labelKey)}</span>
            {badge && badge > 0 ? (
                <>
                    <span aria-hidden="true" className="flex min-w-[1.4rem] items-center justify-center rounded-full bg-action px-1.5 text-[0.7rem] leading-[1.4rem] font-bold text-ink">
                        {badge}
                    </span>
                    {badgeLabel ? <span className="sr-only">{badgeLabel}</span> : null}
                </>
            ) : null}
        </Link>
    )
}

export function SideNav({ className }: { className?: string }) {
    const { t } = useTranslation()
    const { bootstrap } = useRouteContext({ from: '__root__' })
    const threads = useQuery(threadsQuery)
    const notices = useQuery(noticesQuery)
    const unreadMessages = threads.data?.reduce((n, th) => n + th.unreadCount, 0) ?? 0
    const unreadNotices = notices.data?.unreadCount ?? 0
    const logo = bootstrap.branding.logoUrl

    return (
        <aside className={cn('fixed bottom-0 left-0 top-[var(--impersonation-offset,0px)] z-20 w-64 flex-col gap-6 border-r border-line bg-surface px-4 py-6', className)}>
            <Link to="/inicio" className="flex min-h-11 items-center px-2 no-underline">
                {logo ? (
                    <img src={logo} alt={bootstrap.appName} data-impersonation-trigger="" className="h-auto w-36" />
                ) : (
                    <span className="font-display text-lg font-bold text-brand">{bootstrap.appName}</span>
                )}
            </Link>

            <Link
                to="/doar"
                className="flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-accent px-5 text-[0.95rem] font-bold text-ink no-underline shadow-[0_6px_16px_-8px_rgb(0_0_0/0.45)] hover:brightness-95 data-[status=active]:ring-2 data-[status=active]:ring-brand data-[status=active]:ring-offset-2"
            >
                <HandHeart className="size-5" strokeWidth={2.1} aria-hidden="true" />
                {t('sponsorShell.nav.donate')}
            </Link>

            <nav aria-label={t('sponsorShell.nav.label')}>
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {PRIMARY.map(item => (
                        <li key={item.to}>
                            <NavItem
                                item={item}
                                badge={item.to === '/mensagens' ? unreadMessages : undefined}
                                badgeLabel={item.to === '/mensagens' ? t('sponsorShell.nav.unread', { count: unreadMessages }) : undefined}
                            />
                        </li>
                    ))}
                </ul>
            </nav>

            <nav aria-label={t('sponsorShell.nav.moreLabel')} className="mt-auto border-t border-line pt-4">
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {SECONDARY.map(item => (
                        <li key={item.to}>
                            <NavItem
                                item={item}
                                badge={item.to === '/comunicados' ? unreadNotices : undefined}
                                badgeLabel={item.to === '/comunicados' ? t('sponsorHome.noticesUnread', { count: unreadNotices }) : undefined}
                            />
                        </li>
                    ))}
                </ul>
            </nav>
        </aside>
    )
}
